<template>
  <div class="space-y-11">
    <div class="flex flex-row items-center justify-between">
      <span class="text-2xl font-medium sm:text-4xl">{{ __('My Challenges')}}</span>
    </div>
    <TabGroup
        as="div"
        :selectedIndex="selectedIndex"
        @change="handleTabChange">
      <TabList class="hidden sm:flex gap-2 overflow-auto rounded-lg bg-white p-3">
        <Tab v-slot="{ selected }" as="template">
          <button
              :class="selected ? 'bg-secondary/5':'bg-white'"
              class="rounded-md px-9 py-3 font-medium leading-normal text-zinc-800 focus:outline-none outline-none transition-colors hover:bg-secondary/5"
          >
            {{ __('Saved') }}
          </button>
        </Tab>
        <Tab v-slot="{ selected }" as="template">
          <button
              :class="selected? 'bg-secondary/5':'bg-white'"
              class="rounded-md px-9 py-3 font-medium leading-normal text-zinc-800 focus:outline-none outline-none transition-colors hover:bg-secondary/5"
          >
            {{ __('In review') }}
          </button>
        </Tab>
        <Tab v-slot="{ selected }" as="template">
          <button
              :class="selected? 'bg-secondary/5':'bg-white'"
              class="rounded-md px-9 py-3 font-medium leading-normal text-zinc-800 focus:outline-none outline-none transition-colors hover:bg-secondary/5"
          >
            {{ __('Evaluated') }}
          </button>
        </Tab>
      </TabList>

      <div class="flex sm:hidden items-center justify-between bg-white w-full px-2 py-3.5 rounded-lg">
        <button
            v-if="mobileTabIndex > 0"
            @click="previousMobileTab"
            class="flex-shrink-0 mr-2"
        >
          <Icon name="arrow_back_ios" class="w-4 h-5 text-zinc-800" is-filled />
        </button>

        <span class="text-center flex-1 rounded-md font-medium leading-normal text-zinc-800 bg-secondary/5 py-3">
          <span
              :class="[
                selectedIndex === 0 ? 'pl-5' : selectedIndex === 1 ? '' : 'pr-5'
            ]"
          >{{ selectedIndex === 0 ? __('Saved') : selectedIndex === 1 ? __('In Review') : __('Evaluated') }}</span>
        </span>

        <button
            v-if="mobileTabIndex < availableTabs.length - 1"
            @click="nextMobileTab"
            class="flex-shrink-0 ml-2"
        >
          <Icon name="arrow_forward_ios" class="w-4 h-5 text-zinc-800" is-filled />
        </button>
      </div>
      <TabPanels class="bg-white mt-12 p-6 rounded-lg">
        <TabPanel v-for="(tab, index) in availableTabs" :key="tab">
          <EmptyState
              v-if="!getChallengesByTab(index).length"
              :title="emptyStates[index].title"
              :subtitle="emptyStates[index].subtitle"
              :button-text="emptyStates[index].buttonText"
              :button-route="emptyStates[index].buttonRoute"
          />
          <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
            <CandidateChallengeCard
                v-for="ch in getChallengesByTab(index)"
                :key="ch.id"
                :challenge="ch"
                @save="handleSaveChallenge"
                @unsave="handleUnsaveChallenge"
            />
          </div>
        </TabPanel>
      </TabPanels>
    </TabGroup>
  </div>
</template>

<script setup>
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from '@headlessui/vue';
import {__} from "@/helpers";
import CandidateChallengeCard from "@/pages/candidate/challenges/components/CandidateChallengeCard.vue";
import {computed, ref} from "vue";
import axios from "axios";
import { useToast } from 'vue-toastification';
import EmptyState from "@/pages/candidate/challenges/components/EmptyState.vue";
import {Button} from "@/components/block/button";
import {Icon} from "@/components/block/icon";

const props = defineProps({
  data: {
    type: Object,
    required: true
  }
});

const availableTabs = ['saved','in_review','evaluated'];
const selectedIndex = ref(0);
const toast = useToast();

const savedChallenges = computed(() => props.data.challenges.saved || []);
const inReviewChallenges = computed(() => props.data.challenges.in_review || []);
const evaluatedChallenges = computed(() => props.data.challenges.evaluated || []);

const mobileTabIndex = ref(selectedIndex.value);

const nextMobileTab = () => {
  if (mobileTabIndex.value < availableTabs.length - 1) {
    mobileTabIndex.value++;
    selectedIndex.value = mobileTabIndex.value;
    handleTabChange(mobileTabIndex.value);
  }
}

const previousMobileTab = () => {
  if (mobileTabIndex.value > 0){
    mobileTabIndex.value--;
    selectedIndex.value = mobileTabIndex.value;
    handleTabChange(mobileTabIndex.value);
  }
}
function getChallengesByTab(index) {
  switch(index) {
    case 0: return savedChallenges.value;
    case 1: return inReviewChallenges.value;
    case 2: return evaluatedChallenges.value;
    default: return [];
  }
}

const emptyStates = [
  {
    title: __('No Saved Challenges Yet'),
    subtitle: __('Want to keep track of interesting opportunities? Start exploring challenges and save your favorites for later!'),
    buttonText: __('Explore Challenges'),
    buttonRoute: 'challenges.index'
  },
  {
    title: __('No Challenges Under Review Yet!'),
    subtitle: __('Ready to show off your skills? Explore new challenges and submit your best work!'),
    buttonText: 'Explore Challenges',
    buttonRoute: 'challenges.index'
  },
  {
    title: __('No Evaluated Challenges Yet!'),
    subtitle: __(`Keep an eye out! Once a recruiter reviews your submission, you'll see the results here. In the meantime, why not explore new challenges?`),
    buttonText: 'Explore Challenges',
    buttonRoute: 'challenges.index'
  }
];

function handleTabChange(idx) {
  selectedIndex.value = idx;
}

const handleSaveChallenge = async (challenge) => {
  try {
    await axios.post(route('redesign.candidate.challenges.save', {
      challenge: challenge.id
    }));
    toast.success(__('Challenge saved successfully'));
    window.location.reload();
  } catch (error) {
    toast.error(__('Failed to save challenge'));
  }
}

const handleUnsaveChallenge = async (challenge) => {
  try {
    await axios.delete(route('redesign.candidate.challenges.unsave', {
      challenge: challenge.id
    }));
    toast.success(__('Challenge removed from saved list'));
    window.location.reload();
  } catch (error) {
    toast.error(__('Failed to remove challenge'));
  }
}
</script>